import { memo } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface FeedPostSkeletonProps {
  className?: string;
}

/**
 * Skeleton matching FeedPost dimensions exactly to prevent layout shift.
 * Matches the structure: header (avatar + name) -> image(s) -> voting area -> actions
 */
export const FeedPostSkeleton = memo(function FeedPostSkeleton({
  className,
}: FeedPostSkeletonProps) {
  return (
    <div className={cn('p-4 space-y-4', className)}>
      {/* Header: Avatar + Username + Time */}
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="flex-1 space-y-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="w-8 h-8 rounded-full" />
      </div>
      
      {/* Caption */}
      <div className="space-y-1">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
      
      {/* Image area - matches aspect-square for most polls */}
      <Skeleton className="w-full aspect-square rounded-2xl" />
      
      {/* Voting/action buttons */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-4">
          <Skeleton className="w-20 h-9 rounded-full" />
          <Skeleton className="w-20 h-9 rounded-full" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="w-9 h-9 rounded-full" />
          <Skeleton className="w-9 h-9 rounded-full" />
        </div>
      </div>
      
      {/* Vote count */}
      <Skeleton className="h-3 w-20" />
    </div>
  );
});

interface FeedSkeletonProps {
  count?: number;
  className?: string;
}

/**
 * List of FeedPost skeletons for HomePage loading state.
 */
export const FeedSkeleton = memo(function FeedSkeleton({
  count = 3,
  className,
}: FeedSkeletonProps) {
  return (
    <div className={cn('divide-y divide-border/50', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <FeedPostSkeleton key={i} />
      ))}
    </div>
  );
});

export default FeedPostSkeleton;
